<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const active = useMapGetter('painelHumanAttention/getActiveAttendance');
const uiFlags = useMapGetter('painelHumanAttention/getUiFlags');
const accountId = computed(() => route.params.accountId);

const formatPhone = phone => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  const local =
    digits.startsWith('55') && digits.length > 11 ? digits.slice(2) : digits;
  if (local.length === 11)
    return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  if (local.length === 10)
    return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return phone;
};

const label = computed(
  () =>
    active.value?.contact?.name ||
    formatPhone(active.value?.phone_number) ||
    ''
);

const isResolving = computed(
  () => !!(active.value && uiFlags.value.isAttending[active.value.id])
);

const resolve = () => {
  store.dispatch('painelHumanAttention/resolveAttendance');
};

const viewConversation = () => {
  const displayId = active.value?.conversation?.display_id;
  if (!displayId) return;
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: displayId },
  });
};

const viewList = () => {
  router.push(`/app/accounts/${accountId.value}/painel/atendimentos-humanos`);
};
</script>

<template>
  <Teleport to="body">
    <Transition name="badge-slide">
      <div
        v-if="active"
        class="fixed bottom-6 left-1/2 z-[70] flex -translate-x-1/2 items-center gap-3 rounded-2xl border border-n-ruby-7 bg-n-solid-2 px-4 py-3 shadow-2xl"
      >
        <div
          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-n-ruby-3 text-n-ruby-11"
        >
          <i class="i-ph-headset text-base" />
        </div>
        <div class="flex min-w-0 flex-col">
          <span class="text-[11px] font-semibold uppercase tracking-wide text-n-ruby-10">
            {{ t('PAINEL.HUMAN_ATTENTION.ACTIVE.TITLE') }}
          </span>
          <span class="max-w-[180px] truncate text-sm font-medium text-n-slate-12">
            {{ label }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <button
            v-if="active.conversation?.display_id"
            type="button"
            class="rounded-md border border-n-strong px-3 py-1.5 text-xs font-medium text-n-slate-11 transition hover:bg-n-solid-3"
            @click="viewConversation"
          >
            {{ t('PAINEL.HUMAN_ATTENTION.ACTIVE.VIEW_CONVERSATION') }}
          </button>
          <button
            type="button"
            class="rounded-md border border-n-strong px-3 py-1.5 text-xs font-medium text-n-slate-11 transition hover:bg-n-solid-3"
            @click="viewList"
          >
            {{ t('PAINEL.HUMAN_ATTENTION.ACTIVE.VIEW_LIST') }}
          </button>
          <button
            type="button"
            :disabled="isResolving"
            class="flex items-center gap-1.5 rounded-md bg-n-teal-9 px-3.5 py-1.5 text-xs font-semibold text-white transition hover:bg-n-teal-10 disabled:opacity-50"
            @click="resolve"
          >
            <i class="i-ph-check-circle text-sm" />
            {{ t('PAINEL.HUMAN_ATTENTION.ACTIVE.RESOLVE') }}
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.badge-slide-enter-active,
.badge-slide-leave-active {
  transition: all 0.25s ease;
}
.badge-slide-enter-from,
.badge-slide-leave-to {
  opacity: 0;
  transform: translate(-50%, 1rem);
}
</style>
